/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { ReactElement } from "react";

import { connect } from "react-redux";
import { Dispatch } from "redux";

import filter from "lodash/filter";

import * as ActivityEventTypesSelectors from "Dataflows/ActivityEventTypes/ActivityEventTypesSelector";
import * as AwardKitSkuSelectors from "Dataflows/AwardKitSku/AwardKitSkuSelector";
import * as AwardPeriodSelectors from "Dataflows/AwardPeriod/AwardPeriodSelector";
import * as ClRegionsSelectors from "Dataflows/ClRegion/ClRegionSelector";
import * as CommonDataActionTypes from "Dataflows/CommonData/CommonDataAction";
import * as CommonDataSelectors from "Dataflows/CommonData/CommonDataSelector";
import * as CountryRegionSelectors from "Dataflows/CountryRegion/CountryRegionSelector";
import * as IndustryFocusSelectors from "Dataflows/IndustryFocus/IndustryFocusSelector";
import * as RegionSelectors from "Dataflows/Region/RegionSelector";
import * as StateProvinceActions from "Dataflows/StateProvince/StateProvinceAction";
import * as StateProvinceSelectors from "Dataflows/StateProvince/StateProvinceSelector";
import * as TechnicalExpertiseSelectors from "Dataflows/TechnicalExpertise/TechnicalExpertiseSelector";
import * as TechnologyAreaSelectors from "Dataflows/TechnologyArea/TechnologyAreaSelector";

import SideBar from "Components/SideBar/SideBar";
import { FilterValueFieldNames } from "Constants/FilterFields";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  getBenefitNamesLoadError,
  getBenefitNamesOptionList,
} from "Dataflows/Benefit/ManageCodesSlice";
import { AppState } from "Dataflows/Reducers";
import { TechnologyAreaActionTypes } from "Dataflows/TechnologyArea/TechnologyAreaAction";
import { IFilterValue } from "Interfaces/IFilterValue";

import { ISideBarContainerProps } from "./ISideBarContainerProps";

/**
 * Get the selected technology area group names.
 */
export const getSelectedTechAreaGroupNames = (selectedFilterValues: IFilterValue[]): string[] => {
  return filter(
    selectedFilterValues,
    (item) => item.fieldName === FilterValueFieldNames.TECHNOLOGY_AREA_GROUP
  ).map((item) => item.value);
};

/**
 * Sidebar displays filter search and listing.
 * @FunctionComponent
 * @param {ISideBarProps} props The side bar props.
 * @returns {React.FunctionComponent<ISideBarContainerProps>} The side bar component.
 */
const SideBarContainer: React.FunctionComponent<ISideBarContainerProps> = (
  props: ISideBarContainerProps
): ReactElement => {
  const {
    actions: { loadStateProvince },
    awardPeriodOptionList,
    awardKitSkuOptionList,
    awardKitSkuLoadError,
    benefitNamesOptionList,
    benefitNamesLoadError,
    countryRegionOptionList,
    countryRegionsLoadError,
    filterFields,
    selectedFilterValues,
    stateProvinceOptionList,
    stateProvincesLoadError,
    industryFocusOptionList,
    industryFocusLoadError,
    technicalExpertiseOptionList,
    technicalExpertiseLoadError,
    technologyAreaOptionList,
    technologyAreaGroupOptionList,
    technologyAreaLoadError,
    clRegionsOptionList,
    activityEventTypesOptionList,
    activityEventTypesError,
    clRegionsLoadError,
    reviewStatusOptionList,
    availableCpmOptionList,
    title,
    tenant,
    urlWithParam,
    addFilterValue,
    removeFilterCountryValue,
    removeFilterProvinceValue,
    onTechAreaGroupFilterValueChanged,
    removeFilterValue,
    updateFilterValue,
  } = props;

  return (
    <SideBar
      addFilterValue={addFilterValue}
      removeFilterValue={removeFilterValue}
      updateFilterValue={updateFilterValue}
      removeFilterProvinceValue={removeFilterProvinceValue}
      removeFilterCountryValue={removeFilterCountryValue}
      onTechAreaGroupFilterValueChanged={onTechAreaGroupFilterValueChanged}
      selectedFilterValues={selectedFilterValues}
      awardPeriodOptionList={awardPeriodOptionList}
      awardKitSkuOptionList={awardKitSkuOptionList}
      awardKitSkuLoadError={awardKitSkuLoadError}
      benefitNamesOptionList={benefitNamesOptionList}
      benefitNamesLoadError={benefitNamesLoadError}
      countryRegionOptionList={countryRegionOptionList}
      countryRegionsLoadError={countryRegionsLoadError}
      regionOptionList={countryRegionOptionList}
      regionsLoadError={countryRegionsLoadError}
      stateProvinceOptionList={stateProvinceOptionList}
      stateProvincesLoadError={stateProvincesLoadError}
      industryFocusOptionList={industryFocusOptionList}
      industryFocusLoadError={industryFocusLoadError}
      technicalExpertiseOptionList={technicalExpertiseOptionList}
      technicalExpertiseLoadError={technicalExpertiseLoadError}
      technologyAreaOptionList={technologyAreaOptionList}
      technologyAreaGroupOptionList={technologyAreaGroupOptionList}
      technologyAreaLoadError={technologyAreaLoadError}
      clRegionsOptionList={clRegionsOptionList}
      activityEventTypesOptionList={activityEventTypesOptionList}
      activityEventTypesError={activityEventTypesError}
      clRegionsLoadError={clRegionsLoadError}
      loadStateProvince={loadStateProvince}
      reviewStatusOptionList={reviewStatusOptionList}
      availableCpmOptionList={availableCpmOptionList}
      title={title}
      filterFields={filterFields}
      urlWithParam={urlWithParam}
    />
  );
};

/**
 * Maps SideBar to redux state
 * @param {AppState} state The App state.
 * @param {Partial<ISideBarContainerProps>} props The side bar container props.
 */
const mapStateToProps = (state: AppState, props: Partial<ISideBarContainerProps>) => {
  const selectedTechAreaGroups = !!props.selectedFilterValues
    ? getSelectedTechAreaGroupNames(props.selectedFilterValues)
    : [];
  return {
    awardPeriodOptionList: AwardPeriodSelectors.getAwardPeriodOptionList(
      state.awardPeriodDataState
    ),
    awardKitSkuOptionList: AwardKitSkuSelectors.getAwardKitSkuOptionList(
      state.awardKitSkuDataState
    ),
    awardKitSkuLoadError: AwardKitSkuSelectors.getAwardKitSkuLoadError(state.awardKitSkuDataState),
    benefitNamesOptionList: getBenefitNamesOptionList({ manageCodes: state.manageCodes }),
    benefitNamesLoadError: getBenefitNamesLoadError({ manageCodes: state.manageCodes }),
    countryRegionOptionList: CountryRegionSelectors.getCountryRegionsOptionList(
      state.countryRegionDataState
    ),
    countryRegionsLoadError: CountryRegionSelectors.getCountryRegionLoadError(
      state.countryRegionDataState
    ),
    regionsLoadError: RegionSelectors.getRegionLoadError(state.regionDataState),
    regionOptionList: RegionSelectors.getRegionsOptionList(state.regionDataState),
    stateProvinceOptionList: StateProvinceSelectors.getStateProvincesOptionList(
      state.stateProvinceDataState
    ),
    stateProvincesLoadError: StateProvinceSelectors.getStateProvinceLoadError(
      state.stateProvinceDataState
    ),
    industryFocusOptionList: IndustryFocusSelectors.getIndustryFocusOptionList(
      state.industryFocusDataState
    ),
    industryFocusLoadError: IndustryFocusSelectors.getIndustryFocusLoadError(
      state.industryFocusDataState
    ),
    technicalExpertiseOptionList: TechnicalExpertiseSelectors.getTechnicalExpertiseOptionList(
      state.technicalExpertiseDataState
    ),
    technicalExpertiseLoadError: TechnicalExpertiseSelectors.getTechnicalExpertiseLoadError(
      state.technicalExpertiseDataState
    ),
    technologyAreaOptionList: TechnologyAreaSelectors.selectTechnologyAreaBySelectedGroups(
      state.technologyAreaDataState,
      selectedTechAreaGroups
    ),
    activityEventTypesOptionList: ActivityEventTypesSelectors.getActivityEventTypesOptionsList(
      state.activityEventTypesState
    ),
    activityEventTypesError: ActivityEventTypesSelectors.getActivityEventTypesLoadError(
      state.activityEventTypesState
    ),
    technologyAreaGroupOptionList: TechnologyAreaSelectors.getTechnologyAreaGroupOptionList(
      state.technologyAreaDataState
    ),
    technologyAreaLoadError: TechnologyAreaSelectors.getTechnologyAreaLoadError(
      state.technologyAreaDataState
    ),
    clRegionsOptionList: ClRegionsSelectors.getClRegionsOptionList(state.clRegionsDataState),
    clRegionsLoadError: ClRegionsSelectors.getClRegionsLoadError(state.clRegionsDataState),
    reviewStatusOptionList: CommonDataSelectors.getMvpReviewStatusOptionList(state.commonDataState),
    availableCpmOptionList:
      props.tenant == TenantTypesEnum.MSP
        ? CommonDataSelectors.getMlsaAvailableCpmOptionList(state.commonDataState)
        : CommonDataSelectors.getMvpAvailableCpmOptionList(state.commonDataState),
  };
};

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (
  dispatch: Dispatch<
    | StateProvinceActions.StateProvinceActionTypes
    | TechnologyAreaActionTypes
    | CommonDataActionTypes.CommonDataActionTypes
  >
) => ({
  actions: {
    loadStateProvince: (countryRegionName: string): StateProvinceActions.StateProvinceActionTypes =>
      dispatch(StateProvinceActions.getStateProvince(countryRegionName)),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SideBarContainer);
